'use client';

import { useTranslations, useLocale } from 'next-intl';
import { useMemo, useRef, useState, useTransition } from 'react';
import { Check, Globe, Search } from 'lucide-react';
import { auth } from '@/lib/firebase';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { locales, type Locale } from '@/i18n/routing';
import { cn } from '@/lib/utils';

/** Human-readable display names for each locale (in their own language) */
const LOCALE_NAMES: Record<Locale, string> = {
  en: 'English',
  es: 'Español',
  fr: 'Français',
  de: 'Deutsch',
  pt: 'Português',
  it: 'Italiano',
  nl: 'Nederlands',
  sv: 'Svenska',
  da: 'Dansk',
  no: 'Norsk',
  fi: 'Suomi',
  pl: 'Polski',
  ru: 'Русский',
  uk: 'Українська',
  cs: 'Čeština',
  ro: 'Română',
  hu: 'Magyar',
  sk: 'Slovenčina',
  hr: 'Hrvatski',
  sr: 'Српски',
  bg: 'Български',
  sl: 'Slovenščina',
  lt: 'Lietuvių',
  lv: 'Latviešu',
  et: 'Eesti',
  el: 'Ελληνικά',
  tr: 'Türkçe',
  ar: 'العربية',
  he: 'עברית',
  fa: 'فارسی',
  hi: 'हिन्दी',
  bn: 'বাংলা',
  ur: 'اردو',
  ta: 'தமிழ்',
  te: 'తెలుగు',
  mr: 'मराठी',
  gu: 'ગુજરાતી',
  pa: 'ਪੰਜਾਬੀ',
  ml: 'മലയാളം',
  th: 'ภาษาไทย',
  vi: 'Tiếng Việt',
  id: 'Bahasa Indonesia',
  ms: 'Bahasa Melayu',
  tl: 'Filipino',
  ko: '한국어',
  ja: '日本語',
  zh: '中文（简体）',
  'zh-TW': '中文（繁體）',
  my: 'မြန်မာဘာသာ',
  km: 'ភាសាខ្មែរ',
  sw: 'Kiswahili',
  am: 'አማርኛ',
  ca: 'Català',
  // African languages
  ha: 'Hausa',
  yo: 'Yorùbá',
  ig: 'Igbo',
  zu: 'isiZulu',
  xh: 'isiXhosa',
  af: 'Afrikaans',
  so: 'Soomaali',
  om: 'Afaan Oromoo',
  wo: 'Wolof',
  sn: 'ChiShona',
  ny: 'Chichewa',
  ln: 'Lingála',
  rw: 'Kinyarwanda',
  lg: 'Luganda',
  ti: 'ትግርኛ',
  tn: 'Setswana',
  st: 'Sesotho',
  mg: 'Malagasy',
  sg: 'Sängö',
  ak: 'Twi',
};

/** RTL locales — flip text direction */
const RTL_LOCALES = new Set<Locale>(['ar', 'he', 'fa', 'ur']);

function setLocaleCookie(locale: Locale) {
  const maxAge = 60 * 60 * 24 * 365;
  document.cookie = `NEXT_LOCALE=${locale}; path=/; max-age=${maxAge}; samesite=lax`;
}

interface LanguageSwitcherProps {
  /** 'compact' shows only the globe icon; 'full' shows icon + locale name */
  variant?: 'compact' | 'full';
  /** Folded desktop menu: globe on top, the language code under it, centred (Gordon 2026-11-01). */
  stacked?: boolean;
  className?: string;
}

export function LanguageSwitcher({ variant = 'compact', className, stacked = false }: LanguageSwitcherProps) {
  const t = useTranslations('languageSwitcher');
  const [isPending, startTransition] = useTransition();
  // ⚠️ THE iOS crash-loop root cause lived here. The old initializer read
  // document.cookie during render: the server (no document) always rendered
  // 'en' while the client's first hydration render read the real cookie —
  // so every non-English visitor got a hydration text mismatch (React #418)
  // on EVERY page (this component is in the sidebar chrome). On iOS/WebKit
  // the repeated full-page hydration recovery destabilized the renderer
  // into "A problem repeatedly occurred". useLocale() is the server-provided
  // locale — identical on both sides by construction, and it already IS the
  // cookie's value (next-intl resolves it from the same request).
  const serverLocale = useLocale() as Locale;
  const [current, setCurrent] = useState<Locale>(serverLocale);

  const handleSelect = (locale: Locale) => {
    setLocaleCookie(locale);
    setCurrent(locale);
    startTransition(async () => {
      // Persist to the user's profile so the choice is remembered on the next
      // sign-in / other devices (best-effort — the cookie covers this browser).
      try {
        const u = auth.currentUser;
        if (u) {
          const token = await u.getIdToken();
          await fetch('/api/user/language', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ locale }),
          });
        }
      } catch { /* non-fatal — cookie already set */ }
      // Reload so next-intl picks up the new cookie via getRequestConfig
      window.location.reload();
    });
  };

  const isRTL = RTL_LOCALES.has(current);
  const [query, setQuery] = useState('');
  // Phones: the panel starts exactly at the bottom of the header row — from
  // the bottom of the small EN button it covered the header's lower edge
  // (Gordon 2026-10-31: "language pop is cropping header").
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [offset, setOffset] = useState(0);
  const onOpenChange = (open: boolean) => {
    if (!open) return;
    const r = triggerRef.current?.getBoundingClientRect();
    const headerH = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 50;
    setOffset(r && window.innerWidth < 640 ? Math.max(0, Math.round(headerH - r.bottom)) : 0);
  };

  // Each language's name in the CURRENT UI language too ("Svenska · Swedish"),
  // so people can find theirs from either side; the search matches both.
  const localName = useMemo(() => {
    try {
      const dn = new Intl.DisplayNames([current], { type: 'language' });
      return (l: Locale) => { try { return dn.of(l) || ''; } catch { return ''; } };
    } catch { return () => ''; }
  }, [current]);
  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return locales;
    return locales.filter(l => [l, LOCALE_NAMES[l], localName(l)].some(v => v.toLowerCase().includes(q)));
  }, [query, localName]);

  return (
    <DropdownMenu onOpenChange={onOpenChange}>
      <DropdownMenuTrigger asChild>
        <Button
          ref={triggerRef}
          variant="outline"
          size="sm"
          className={cn(
            // Visible high-contrast outline so it reads as CLICKABLE
            // (Gordon: "outline language buttons so one understands better
            // you can click on it").
            'gap-1.5 border-2 border-foreground/40 text-foreground hover:border-foreground/70',
            stacked && 'flex-col items-center justify-center gap-0.5',
            isPending && 'opacity-50',
            className,
          )}
          aria-label={t('selectLanguage')}
        >
          {/* Folded menu: the same size as the menu's icons, the code under it, about as wide. */}
          <Globe className={cn('flex-shrink-0', stacked ? 'h-6 w-6' : 'h-4 w-4')} />
          {variant === 'full' && (
            <span className="text-xs font-medium" dir={isRTL ? 'rtl' : 'ltr'}>
              {LOCALE_NAMES[current]}
            </span>
          )}
          {variant === 'compact' && (
            <span className={cn('font-medium uppercase', stacked ? 'text-[13px] font-semibold leading-none tracking-[0.12em]' : 'text-xs')}>{current.slice(0, 2)}</span>
          )}
        </Button>
      </DropdownMenuTrigger>

      {/* Theme colours throughout (Gordon 2026-10-31: "doesn't look so nice,
          improve design and make sure it follows themes"). Phones: full width,
          from the header down to the player — "full body minus header and
          footer"; desktop: a comfortable panel. */}
      <DropdownMenuContent
        align="end"
        sideOffset={offset}
        collisionPadding={0}
        onCloseAutoFocus={() => setQuery('')}
        className={cn(
          'z-[110] flex flex-col overflow-hidden border-border bg-popover p-0 text-popover-foreground shadow-2xl',
          'max-sm:h-[calc(100dvh_-_var(--header-height)_-_56px)] max-sm:w-screen max-sm:rounded-none max-sm:border-x-0',
          'sm:max-h-[min(36rem,70vh)] sm:w-80 sm:rounded-xl',
        )}
      >
        <div className="flex flex-col gap-2 border-b border-border p-3">
          <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-foreground">
            <Globe className="h-4 w-4" aria-hidden />
            {t('selectLanguage')}
          </p>
          <label className="relative block">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
            <input
              value={query}
              onChange={e => setQuery(e.target.value)}
              // The menu's type-to-select would steal these keys.
              onKeyDown={e => { if (!['ArrowDown', 'ArrowUp', 'Escape', 'Tab'].includes(e.key)) e.stopPropagation(); }}
              placeholder={t('searchPlaceholder')}
              aria-label={t('searchPlaceholder')}
              className="h-10 w-full rounded-lg border border-input bg-background pl-9 pr-3 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
          </label>
        </div>
        <div className="grid flex-1 auto-rows-min grid-cols-2 gap-1 overflow-y-auto overscroll-contain p-2 sm:grid-cols-1">
          {shown.map(locale => {
            const selected = locale === current;
            const other = localName(locale);
            return (
              <DropdownMenuItem
                key={locale}
                onClick={() => handleSelect(locale)}
                aria-current={selected ? 'true' : undefined}
                dir={RTL_LOCALES.has(locale) ? 'rtl' : 'ltr'}
                className={cn(
                  'min-w-0 cursor-pointer gap-3 rounded-lg px-3 py-2.5',
                  selected
                    ? 'bg-primary font-semibold text-primary-foreground focus:bg-primary focus:text-primary-foreground'
                    : 'text-foreground focus:bg-muted focus:text-foreground',
                )}
              >
                <span className={cn('w-7 shrink-0 text-2xs font-bold uppercase', selected ? 'opacity-90' : 'text-muted-foreground')}>{locale}</span>
                <span className="flex min-w-0 flex-1 flex-col leading-tight">
                  <span className="truncate text-sm">{LOCALE_NAMES[locale]}</span>
                  {other && other !== LOCALE_NAMES[locale] && (
                    <span className={cn('truncate text-2xs', selected ? 'opacity-90' : 'text-muted-foreground')}>{other}</span>
                  )}
                </span>
                {selected && <Check className="h-4 w-4 shrink-0" aria-hidden />}
              </DropdownMenuItem>
            );
          })}
          {shown.length === 0 && (
            <p className="col-span-full px-3 py-6 text-center text-sm text-muted-foreground">{t('noMatch')}</p>
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
